import type { VariantProps } from 'class-variance-authority'
import * as React from 'react'
import {
  createContext,
  PropsWithChildren,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react'

import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import {
  Button,
  buttonVariants,
} from '@/components/ui/button'

type InfoDialogOptions = {
  title?: string
  description?: string
  closeVariant?: VariantProps<typeof buttonVariants>['variant']
  closeSize?: VariantProps<typeof buttonVariants>['size']
  closeText?: string
}

type InfoDialogContextType = {
  info: (options: InfoDialogOptions) => void
}

const InfoDialogContext = createContext<InfoDialogContextType | undefined>(undefined)

export function InfoDialogProvider({ children }: PropsWithChildren) {
  const [open, setOpen] = useState(false)
  const [{
    title,
    description,
    closeText = 'Close',
    closeVariant = 'outline',
    closeSize = 'sm',
  }, setOptions] = useState<InfoDialogOptions>({})

  const value = useMemo(() => ({
    info: (opts: InfoDialogOptions) => {
      setOptions(opts)
      setOpen(true)
    },
  }), [])

  const handleClose = useCallback(async () => setOpen(false), [])

  return (
    <InfoDialogContext.Provider value={value}>
      {children}
      <AlertDialog open={open}>
        <AlertDialogContent>
          <AlertDialogTitle>{title}</AlertDialogTitle>
          {description && <AlertDialogDescription>{description}</AlertDialogDescription>}
          <div className="flex justify-end mt-4">
            <Button onClick={handleClose} variant={closeVariant} size={closeSize}>
              {closeText}
            </Button>
          </div>
        </AlertDialogContent>
      </AlertDialog>
    </InfoDialogContext.Provider>
  )
}

export function useInfoDialog() {
  const ctx = useContext(InfoDialogContext)
  if (!ctx) throw new Error('useInfoDialog must be used within InfoDialogContext')
  return ctx.info
}
